// app/javascript/controllers/listings_map_controller.js
import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import MapboxDraw from "@mapbox/mapbox-gl-draw"
import { formatArea, formatMoney, formatRentRate } from "../lib/intl_format"

export default class extends Controller {
  static targets = ["map", "aoiPanel", "aoiName", "aoiChannel", "aoiScraped", "aoiError"]
  static values  = { accessToken: String, locale: String, signedIn: Boolean }

  connect() {
    mapboxgl.accessToken = this.accessTokenValue

    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: "mapbox://styles/mapbox/outdoors-v12",
      center: [10, 20],  // world view — global marketplace, no home-country bias
      zoom: 1.5
    })

    this.map.on("load", () => {
      this.loadListings()
      if (this.signedInValue) this.loadAois()
    })

    this.popups = {}
  }

  async loadListings() {
    const bounds = this.map.getBounds()
    const bbox   = [
      bounds.getWest(), bounds.getSouth(),
      bounds.getEast(), bounds.getNorth()
    ].join(",")

    const res  = await fetch(`/map/listings.json?bbox=${bbox}`)
    const data = await res.json()

    if (this.map.getSource("listings")) {
      this.map.getSource("listings").setData(data)
    } else {
      this.map.addSource("listings", { type: "geojson", data, cluster: true, clusterMaxZoom: 12 })

      // Clustered circles
      this.map.addLayer({
        id: "clusters",
        type: "circle",
        source: "listings",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#4f46e5",
          "circle-radius": ["step", ["get", "point_count"], 20, 10, 28, 50, 34],
          "circle-opacity": 0.85
        }
      })

      this.map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "listings",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-size": 12
        },
        paint: { "text-color": "#fff" }
      })

      // Individual pins
      this.map.addLayer({
        id: "listing-pins",
        type: "circle",
        source: "listings",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": [
            "match", ["get", "listing_type"],
            "rent_only",     "#f59e0b",
            "sale_only",     "#10b981",
            "rent_and_sale", "#6366f1",
            "#6b7280"
          ],
          "circle-radius": 10,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#fff"
        }
      })

      // Zoom into cluster on click
      this.map.on("click", "clusters", (e) => {
        const features = this.map.queryRenderedFeatures(e.point, { layers: ["clusters"] })
        const clusterId = features[0].properties.cluster_id
        this.map.getSource("listings").getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return
          this.map.easeTo({ center: features[0].geometry.coordinates, zoom })
        })
      })

      // Popup on individual pin click
      this.map.on("click", "listing-pins", (e) => {
        const props = e.features[0].properties
        const coords = e.features[0].geometry.coordinates.slice()

        const html = `
          <div class="text-sm max-w-[220px]">
            ${props.photo_url ? `<img src="${props.photo_url}" class="w-full h-28 object-cover rounded-t-lg -mx-3 -mt-3 mb-2" />` : ""}
            <p class="font-semibold text-gray-900 leading-tight">${props.title}</p>
            <p class="text-xs text-gray-500 mt-0.5">${formatArea(props.area_sqm)} · ${props.land_use_label || ""}</p>
            ${props.rent_amount_cents  ? `<p class="text-xs mt-1">🌍 ${formatRentRate(props)}</p>` : ""}
            ${props.asking_price_cents ? `<p class="text-xs mt-0.5 text-emerald-700">💰 ${formatMoney(props.asking_price_cents, props.currency)}</p>` : ""}
            <a href="/listings/${props.id}" class="block mt-2 text-indigo-600 text-xs font-medium hover:underline">View listing →</a>
          </div>
        `

        new mapboxgl.Popup({ offset: 16, maxWidth: "260px" })
          .setLngLat(coords)
          .setHTML(html)
          .addTo(this.map)
      })

      this.map.on("mouseenter", "listing-pins",   () => { this.map.getCanvas().style.cursor = "pointer" })
      this.map.on("mouseleave", "listing-pins",   () => { this.map.getCanvas().style.cursor = "" })
      this.map.on("mouseenter", "clusters",       () => { this.map.getCanvas().style.cursor = "pointer" })
      this.map.on("mouseleave", "clusters",       () => { this.map.getCanvas().style.cursor = "" })

      // Reload on map move
      this.map.on("moveend", () => this.loadListings())
    }
  }

  // ── Areas of interest: draw a polygon, get notified of new listings ──────

  // "Draw alert area" button — lazily attach Mapbox GL Draw, enter polygon mode
  startAoiDraw(event) {
    event.preventDefault()
    if (!this.draw) {
      this.draw = new MapboxDraw({ displayControlsDefault: false, controls: {} })
      this.map.addControl(this.draw)
      this.map.on("draw.create", () => this.showAoiPanel())
    }
    this.draw.deleteAll()
    this.hideAoiPanel()
    this.draw.changeMode("draw_polygon")
    this.map.getCanvas().style.cursor = "crosshair"
  }

  showAoiPanel() {
    this.map.getCanvas().style.cursor = ""
    if (this.hasAoiPanelTarget) {
      this.aoiPanelTarget.classList.remove("hidden")
      this.aoiNameTarget.focus()
    }
  }

  hideAoiPanel() {
    if (this.hasAoiPanelTarget) this.aoiPanelTarget.classList.add("hidden")
    if (this.hasAoiErrorTarget) this.aoiErrorTarget.textContent = ""
  }

  cancelAoi(event) {
    event.preventDefault()
    this.draw?.deleteAll()
    this.hideAoiPanel()
  }

  async saveAoi(event) {
    event.preventDefault()
    const features = this.draw?.getAll()?.features || []
    if (!features.length) return this.hideAoiPanel()

    const res = await fetch("/areas_of_interest.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify({
        area_of_interest: {
          name: this.aoiNameTarget.value,
          channel: this.aoiChannelTarget.value,
          include_scraped: this.aoiScrapedTarget.checked,
          geometry_geojson: JSON.stringify(features[0].geometry)
        }
      })
    })

    if (res.ok) {
      this.draw.deleteAll()
      this.hideAoiPanel()
      this.aoiNameTarget.value = ""
      this.loadAois()
    } else {
      const data = await res.json().catch(() => ({}))
      if (this.hasAoiErrorTarget) {
        this.aoiErrorTarget.textContent = (data.errors || []).join(", ") || "Error"
      }
    }
  }

  // Overlay of the user's saved areas — translucent indigo with name labels
  async loadAois() {
    try {
      const res = await fetch("/areas_of_interest.json", { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const data = await res.json()

      if (this.map.getSource("aois")) {
        this.map.getSource("aois").setData(data)
        return
      }

      this.map.addSource("aois", { type: "geojson", data })
      this.map.addLayer({
        id: "aois-fill", type: "fill", source: "aois",
        paint: { "fill-color": "#6366f1", "fill-opacity": 0.08 }
      })
      this.map.addLayer({
        id: "aois-outline", type: "line", source: "aois",
        paint: { "line-color": "#6366f1", "line-width": 1.5, "line-dasharray": [2, 2] }
      })
      this.map.addLayer({
        id: "aois-labels", type: "symbol", source: "aois",
        layout: { "text-field": ["get", "name"], "text-size": 11 },
        paint: { "text-color": "#4f46e5", "text-halo-color": "#fff", "text-halo-width": 1 }
      })
    } catch {
      // Overlay is decorative — never break the search map over it
    }
  }

  // Called from listing card hover
  highlightPin(event) {
    const id = parseInt(event.currentTarget.dataset.listingId)
    this.map.setFilter("listing-pins-highlighted", ["==", "id", id])
  }

  unhighlightPin() {
    this.map.setFilter("listing-pins-highlighted", ["==", "id", -1])
  }

  disconnect() {
    this.map?.remove()
  }
}
